import * as React from 'react';

import { once } from 'lodash';
import pluralize from 'pluralize';

import AdminSchoolSisSyncHeader from 'admin/rostering/components/AdminSchoolSisSyncHeader';
import {
  CleverSyncHoldCopy,
  SftpSyncHoldCopy,
  SyncHold,
  SyncHoldInformation,
  NextStep,
} from 'admin/constants/SyncHoldCopy';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Copy from 'components/Copy';
import Heading from 'components/Heading';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import LinkConstants from 'constants/LinkConstants';
import EventTracking from 'lib/EventTracking';

import AdminSisSyncHoldViewResumeModal from 'admin/rostering/components/AdminSisSyncHoldViewResumeModal';

import {
  AdminSchoolSisDashboard_organizations_sisSyncOverview_holdSummary as HoldSummary,
  AdminSchoolSisDashboard_organizations_sisSyncOverview_holdSummary_affectedResourcesSummary as AffectedResourceSummary,
} from 'admin/rostering/queries/__generated__/AdminSchoolSisDashboard';

import './styles/AdminSisSyncHoldView.scss';

type SyncMethod = 'Clever' | 'SFTP';

type Props = {
  orgUuid: string;
  holdSummary: HoldSummary;
  syncMethod: string;
};

export default class AdminSisSyncHoldView extends React.Component<Props> {
  componentWillMount() {
    this.trackView();
  }

  renderHeader() {
    const subtext =
      'The most recent sync has been placed on hold. Review the following to resolve the issue and continue syncing.';

    return <AdminSchoolSisSyncHeader subtext={subtext} status="on hold" />;
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.hold.view', {
      organization_uud: this.props.orgUuid,
    });
  });

  handleLinkClicked = () => {
    EventTracking.track('rostering.hold.downloaded', {
      organization_uuid: this.props.orgUuid,
    });
  };

  errorText(
    hold: SyncHold,
    affectedResourcesSummary: AffectedResourceSummary[],
    affectedResourceCount: number
  ) {
    return affectedResourcesSummary
      .map((r: AffectedResourceSummary) => {
        return `${r.count} ${pluralize(r.resource, r.count)}`;
      })
      .join(', ');
  }

  continueButtonText(hold: string, affectedResourceCount: number) {
    if (hold === 'TooManyDestructiveChangesHold') {
      return `Confirm ${affectedResourceCount} ${pluralize('removal', affectedResourceCount)}`;
    }
    return `Create ${affectedResourceCount} ${pluralize('account', affectedResourceCount)}`;
  }

  modalTitleText(hold: string, affectedResourceCount: number) {
    if (hold === 'TooManyDestructiveChangesHold') {
      return `Confirm ${pluralize('removal', affectedResourceCount)}?`;
    }
    return `Create ${pluralize('account', affectedResourceCount)}?`;
  }

  fetchSyncHoldCopy(syncMethod: SyncMethod, hold: SyncHold): SyncHoldInformation | undefined {
    if (syncMethod === 'Clever') {
      return CleverSyncHoldCopy[hold];
    } else {
      return SftpSyncHoldCopy[hold];
    }
  }

  renderResourceSummary(error: string, holdSummary: HoldSummary) {
    const {
      hold,
      affectedResourceCount,
      affectedResourcesSummary,
      reportDownloadUrl,
    } = holdSummary;

    return (
      <BoxBody dark size="small">
        <Text size="medium" lineHeight="large" weight="semibold">
          <MediaObject inline>
            <Text>{error}:</Text>
            <MediaObject inline>
              <Text weight="normal">
                {this.errorText(hold as SyncHold, affectedResourcesSummary, affectedResourceCount)}
              </Text>
              <Link
                href={reportDownloadUrl}
                download
                onClick={this.handleLinkClicked}>
                <TextWithIcon
                  icon="arrow-down-bar"
                  gutter="xxsmall"
                  iconSize="medium"
                  appendIcon
                  inline>
                  Download CSV
                </TextWithIcon>
              </Link>
            </MediaObject>
          </MediaObject>
        </Text>
      </BoxBody>
    );
  }

  renderHoldHeader(reason: string) {
    return (
      <BoxBody size="small" className="AdminSisSyncHoldView-reason">
        <Heading level="2" levelStyle="3">
          <Text color="white">Sync paused</Text>
        </Heading>
        <Copy>
          <Text color="white" size="medium">
            {reason}
          </Text>
        </Copy>
      </BoxBody>
    );
  }

  renderNextStep(nextStep: NextStep) {
    return (
      <BoxBody size="small" divided className="AdminSisSyncHoldView-next">
        <Heading level="2" levelStyle="2">
          Next Step
        </Heading>
        <Copy>
          <Text size="medium">
            <p>{nextStep.intro}</p>
            {nextStep.bullets && (
              <ul>
                {nextStep.bullets.map(text => {
                  return (
                    <li>
                      <p>{text}</p>
                    </li>
                  );
                })}
              </ul>
            )}
            {nextStep.footer && <p>{nextStep.footer}</p>}
          </Text>
        </Copy>
      </BoxBody>
    );
  }

  renderHowToFixMyFilesButton() {
    return (
      <Button href={LinkConstants.ROSTERING_HOLDS} target="_blank">
        How to fix my files
      </Button>
    );
  };

  renderCheckCleverDataButton(orgUuid: string, hold: SyncHold) {
    return (
      <ModalTrigger
        modal={
          <AdminSisSyncHoldViewResumeModal
            titleText="Did you fix the errors?"
            subtext="We’ll re-fetch the new data. It will take some time for it to process."
            holdKey={hold}
            orgUuid={orgUuid}
          />
        }>
        <Button>
          {hold === 'CleverDisconnectedHold'
            ? 'I fixed it'
            : 'Check for new Clever data'}
        </Button>
      </ModalTrigger>
    );
  }

  renderResumeButton(orgUuid: string, holdSummary: HoldSummary) {
    const {
      hold,
      temporarilyIgnoreable,
      permanentlyIgnoreable,
      affectedResourceCount,
    } = holdSummary;

    const supportLink = (
      <Link href={LinkConstants.SIS_SYNC_HELP} target="_blank" weight="semibold">
        contact support
      </Link>
    );

    return (
      <ModalTrigger
        modal={
          <AdminSisSyncHoldViewResumeModal
            titleText={this.modalTitleText(hold, affectedResourceCount)}
            subtext={(
              <span>
                This action can’t be undone. If you're not certain that you're ready to proceed, please{' '}
                {supportLink} to discuss your data first.
              </span>
            )}
            continueButtonText={this.continueButtonText(hold, affectedResourceCount)}
            holdKey={hold}
            orgUuid={orgUuid}
            permanentlyIgnoreable={permanentlyIgnoreable}
            temporarilyIgnored
          />
        }>
        <Button color="lava" level="secondary">
          {this.continueButtonText(hold, affectedResourceCount)}
        </Button>
      </ModalTrigger>
    );
  }

  renderHoldFooter(orgUuid: string, syncMethod: SyncMethod, holdSummary: HoldSummary) {
    const { temporarilyIgnoreable } = holdSummary;

    return (
      <BoxFooter size="small" className="AdminSisSyncHoldView-footer">
        <MediaObject center>
          <Buttons align="left">
            {syncMethod === 'SFTP' && this.renderHowToFixMyFilesButton()}
            {syncMethod === 'Clever' && this.renderCheckCleverDataButton(orgUuid, holdSummary.hold as SyncHold)}
            {temporarilyIgnoreable && this.renderResumeButton(orgUuid, holdSummary)}
          </Buttons>
          <Copy>
            <Text>
              If you aren’t sure what to do,{' '}
              <Link href={LinkConstants.SIS_SYNC_HELP} target="_blank" weight="semibold">
                contact support.
              </Link>
            </Text>
          </Copy>
        </MediaObject>
      </BoxFooter>
    );
  }

  render() {
    const { orgUuid, syncMethod, holdSummary } = this.props;
    const { affectedResourceCount, hold } = holdSummary;
    const syncHoldInformation = this.fetchSyncHoldCopy(syncMethod as SyncMethod, hold as SyncHold);

    if (!syncHoldInformation) {
      return null;
    }

    const { reason, nextStep, error } = syncHoldInformation;

    return (
      <section className="AdminSisSyncHoldView">
        {this.renderHeader()}
        <Box textAlign="left">
          {this.renderHoldHeader(reason)}
          {affectedResourceCount > 0 && this.renderResourceSummary(error, holdSummary)}
          {this.renderNextStep(nextStep)}
          {this.renderHoldFooter(orgUuid, syncMethod as SyncMethod, holdSummary)}
        </Box>
      </section>
    );
  }
}
